import React, { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';

const MediaGallery = ({ images = [], videos = [], productName = '' }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [failedImages, setFailedImages] = useState(new Set());
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  // Helper to get absolute URL
  const getAbsoluteUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}${url}`;
  };

  // Combine all media items and filter out failed ones
  const allMedia = [
    ...images.map((url, idx) => ({ type: 'image', url: getAbsoluteUrl(url), id: `img-${idx}` })),
    ...videos.map((url, idx) => ({ type: 'video', url: getAbsoluteUrl(url), id: `vid-${idx}` }))
  ].filter(item => item.url && !failedImages.has(item.url));

  const handleImageError = useCallback((url) => {
    console.warn('Image failed to load, removing from gallery:', url);
    setFailedImages(prev => new Set([...prev, url]));
  }, []);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap());
  }, [emblaApi, emblaThumbsApi]);

  const onThumbClick = useCallback(
    (index) => {
      if (!emblaApi || !emblaThumbsApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi, emblaThumbsApi]
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  // Reset to first image when media changes
  useEffect(() => {
    if (emblaApi && selectedIndex >= allMedia.length && allMedia.length > 0) {
      emblaApi.scrollTo(0);
    }
  }, [allMedia.length, emblaApi, selectedIndex]);

  const getVideoEmbedUrl = (url) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }
    if (url.includes('vimeo.com')) {
      const videoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
    }
    return url;
  };

  const isUploadedVideo = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return (url.includes('/uploads/') || url.includes('/api/uploads/')) && 
           (lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.mov') || lowerUrl.endsWith('.avi') || 
            lowerUrl.endsWith('.webm') || lowerUrl.endsWith('.m4v'));
  };

  const getVideoType = (url) => {
    if (url.endsWith('.mp4') || url.endsWith('.MP4')) return 'video/mp4';
    if (url.endsWith('.mov') || url.endsWith('.MOV')) return 'video/mp4';
    if (url.endsWith('.avi')) return 'video/x-msvideo';
    return 'video/mp4';
  };

  if (allMedia.length === 0) {
    return (
      <div className="aspect-square bg-gray-100 flex items-center justify-center">
        <p className="text-gray-400">No images available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Display */}
      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {allMedia.map((item, index) => (
              <div key={item.id} className="flex-[0_0_100%] min-w-0">
                <div className="aspect-square bg-gray-100">
                  {item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt={`${productName} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={() => handleImageError(item.url)}
                    />
                  ) : isUploadedVideo(item.url) ? (
                    <div className="relative w-full h-full bg-black">
                      <video
                        controls
                        controlsList="nodownload"
                        className="w-full h-full object-contain"
                        preload="metadata"
                        playsInline
                      >
                        <source src={`${item.url}#t=0.1`} type={getVideoType(item.url)} />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ) : (
                    <iframe
                      src={getVideoEmbedUrl(item.url)}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={`Video ${index + 1}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows */}
        {allMedia.length > 1 && (
          <>
            <button
              onClick={scrollPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
              aria-label="Previous"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={scrollNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
              aria-label="Next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
          {selectedIndex + 1} / {allMedia.length}
        </div>
      </div>

      {/* Thumbnails */}
      {allMedia.length > 1 && (
        <div className="overflow-hidden" ref={emblaThumbsRef}>
          <div className="flex gap-2">
            {allMedia.map((item, index) => (
              <button
                key={item.id}
                onClick={() => onThumbClick(index)}
                className={`flex-[0_0_20%] min-w-0 max-w-[120px] aspect-square border-2 transition-all ${
                  index === selectedIndex ? 'border-black' : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                {item.type === 'image' ? (
                  <img
                    src={item.url}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(item.url)}
                  />
                ) : isUploadedVideo(item.url) ? (
                  <div className="relative w-full h-full bg-black">
                    <video
                      className="w-full h-full object-cover"
                      preload="metadata"
                      muted
                      playsInline
                    >
                      <source src={`${item.url}#t=0.1`} type={getVideoType(item.url)} />
                    </video>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                      <Play className="w-8 h-8 text-white drop-shadow-lg" fill="white" />
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <Play className="w-8 h-8 text-gray-600" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
